<script lang="ts" setup>
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogOverlay,
  Menu,
  MenuButton,
  MenuItems,
  MenuItem,
} from '@headlessui/vue'
import { type NavigationNode, getNavigationItems } from './navigation'
import CommandPalette from '~/components/CommandPalette.vue'
import KnowledgeBaseButton from '~/components/knowledge-base/KnowledgeBaseButton.vue'
import KnowledgeBaseSidebar from '~/components/knowledge-base/KnowledgeBaseSidebar.vue'
import { useKnowledgeBase } from '~/composables/useKnowledgeBase'
import { TranslationDashboardDocument } from '~~/graphql/operations'

const NavigationMenu: NavigationNode[] = [
  {
    name: 'Dashboard',
    to: 'index',
    icon: 'i-icon-park-outline:dashboard',
    exact: true,
  },
  {
    name: 'Products',
    icon: 'i-icon-park-outline:ad-product',
    permissions: [
      'product',
      'productInternalStatus',
      'attribute',
      'category',
      'manufacturer',
      'productFilter',
      'productFamily',
      'materialSample',
      'productReview',
      'promotion',
      'promotionBar',
      'coupon',
      'flag',
    ],
    children: [
      {
        name: 'Products',
        to: 'products',
        icon: 'i-icon-park-outline:ad-product',
        permissions: ['product'],
      },
      {
        name: 'Missing Products',
        to: 'products-missingProducts',
        icon: 'i-icon-park-outline:file-question',
        permissions: ['product'],
      },
      {
        name: 'Hidden Products',
        to: 'products-hiddenProducts',
        icon: 'i-icon-park-outline:preview-close-one',
        permissions: ['product'],
      },
      {
        name: 'Attributes',
        to: 'attributes',
        icon: 'i-icon-park-outline:tag-one',
        permissions: ['attribute'],
      },
      {
        name: 'Attribute groups',
        to: 'attribute-groups',
        icon: 'i-icon-park-outline:list-checkbox',
        permissions: ['attributeGroup'],
      },
      {
        name: 'Manufacturers',
        to: 'manufacturers',
        permissions: ['manufacturer'],
        icon: 'i-icon-park-outline:factory-building',
      },
      {
        name: 'Categories',
        to: 'categories',
        icon: 'i-icon-park-outline:folder-open',
        permissions: ['category'],
      },
      {
        name: 'Product Filters',
        to: 'product-filters',
        icon: 'i-icon-park-outline:filter',
        permissions: ['productFilter'],
      },
      {
        name: 'Internal Product Statuses',
        to: 'product-internal-statuses',
        icon: 'i-icon-park-outline:tag-one',
        permissions: ['productInternalStatus'],
      },
      {
        name: 'Product Families',
        to: 'product-families',
        icon: 'i-icon-park-outline:three-hexagons',
        permissions: ['productFamily'],
      },
      {
        name: 'Material Samples',
        to: 'products-materialSamples',
        icon: 'i-icon-park-outline:bookmark',
        permissions: ['materialSample'],
      },
      {
        name: 'Product Reviews',
        to: 'products-reviews',
        icon: 'i-icon-park-outline:message-emoji',
        permissions: ['productReview'],
      },
      {
        name: 'Promotions',
        to: 'products-promotions',
        icon: 'i-icon-park-outline:percentage',
        permissions: ['promotion'],
      },
      {
        name: 'Promotion Bars',
        to: 'products-promotions-bars',
        icon: 'i-heroicons-solid-dots-horizontal',
        permissions: ['promotionBar'],
      },
      {
        name: 'Exact Promotions',
        to: 'products-promotions-exact-promotions',
        icon: 'i-heroicons-solid-cube-transparent',
        permissions: ['promotion'],
      },
      {
        name: 'Coupon Codes',
        to: 'products-coupons',
        icon: 'i-icon-park-outline:paper-money-two',
        permissions: ['coupon'],
      },
      {
        name: 'Flags',
        to: 'flags',
        icon: 'i-icon-park-outline:label',
        permissions: ['flag'],
        beta: true,
      },
      {
        name: 'Lowest Prices',
        to: 'products-lowestPrices',
        icon: 'i-icon-park-outline:shopping-cart-del',
        permissions: ['lowestPrice'],
      },
      {
        name: 'Price Syncs',
        to: 'products-price-syncs',
        icon: 'i-icon-park-outline:data-switching',
        permissions: ['priceSync'],
      },
      {
        name: 'Customer Group Prices',
        to: 'products-customerGroupPrices',
        icon: 'i-icon-park-outline:open-an-account',
        permissions: ['customerGroupPrice'],
      },
      {
        name: 'Configurator Groups',
        to: 'products-configurator-groups',
        icon: 'i-icon-park-outline:multi-rectangle',
        permissions: ['product'],
      },
      {
        name: 'Listing Dividers',
        to: 'listing-dividers',
        icon: 'i-icon-park-outline:division',
        permissions: ['listingsDivider'],
      },
      {
        name: 'Info Boxes',
        to: 'products-info-box',
        icon: 'i-icon-park-outline:box',
        permissions: ['productInfoBox'],
      },
    ],
  },
  {
    name: 'Customers',
    permissions: ['customer'],
    icon: 'i-icon-park-outline:every-user',
    children: [
      {
        name: 'Customers',
        to: 'customers',
        icon: 'i-icon-park-outline:user',
        permissions: ['customer'],
      },
      {
        name: 'Customer Groups',
        to: 'customer-groups',
        icon: 'i-icon-park-outline:every-user',
        permissions: ['customer'],
      },
    ],
  },
  {
    name: 'OMS',
    permissions: ['order'],
    icon: 'i-icon-park-outline:shopping-cart-one',
    children: [
      {
        name: 'Orders',
        to: 'orders',
        icon: 'i-icon-park-outline:weixin-market',
        permissions: ['order'],
      },
    ],
  },
  {
    name: 'Exports',
    to: 'exports',
    icon: 'i-icon-park-outline:download',
    permissions: ['export'],
  },
  {
    name: 'Imports',
    to: 'imports',
    icon: 'i-icon-park-outline:upload-one',
    permissions: ['import'],
  },
  {
    name: 'CMS (Module Groups)',
    to: 'module-groups',
    icon: 'i-icon-park-outline:puzzle',
    permissions: ['module'],
    beta: true,
  },
  {
    name: 'Inspiration Images',
    to: 'inspiration-images',
    icon: 'i-icon-park-outline:picture-album',
    permissions: ['inspirationImage'],
  },
  {
    name: 'Inspiration Pages',
    to: 'inspirations',
    icon: 'i-icon-park-outline:platte',
    permissions: ['inspiration'],
  },
  // {
  //   name: 'Collections',
  //   icon: 'i-icon-park-outline:star',
  //   permissions: ['collection', 'trendshow'],
  //   children: [
  //     {
  //       name: 'Collections',
  //       to: 'collections',
  //       icon: 'i-icon-park-outline:star',
  //       permissions: ['collection'],
  //     },
  //     {
  //       name: 'Trendshows',
  //       to: 'trendshows',
  //       icon: 'i-icon-park-outline:star',
  //       permissions: ['trendshow'],
  //     },
  //   ],
  // },
  {
    name: 'Pages',
    to: 'pages',
    icon: 'i-icon-park-outline:page',
    permissions: ['page'],
  },
  {
    name: 'Showrooms',
    to: 'showrooms',
    icon: 'i-icon-park-outline:shop',
    permissions: ['showroom'],
  },
  {
    name: 'Jobs',
    to: 'jobs',
    icon: 'i-mdi:wizard-hat',
    permissions: ['job'],
  },
  {
    name: 'Reports & Statistics',
    icon: 'i-icon-park-outline:arithmetic-buttons',
    permissions: ['report', 'statistics'],
    children: [
      {
        name: 'Reports',
        to: 'reports',
        icon: 'i-icon-park-outline:arithmetic-buttons',
        permissions: ['report'],
      },
      {
        name: 'Order Statistics',
        to: 'statistics-order',
        icon: 'i-icon-park-outline:arithmetic-buttons',
        permissions: ['statistics'],
      },
      {
        name: 'Customer Statistics',
        to: 'statistics-customer',
        icon: 'i-icon-park-outline:arithmetic-buttons',
        permissions: ['statistics'],
      },
    ],
  },
  {
    name: 'Stocks',
    icon: 'i-icon-park-outline:inbox',
    permissions: [
      'stock',
      'stockPool',
      'stockSourceFile',
      'stockSourceFileDownload',
      'productStock',
      'productStockLog',
    ],
    children: [
      {
        name: 'Stock Pools',
        to: 'stock-pools',
        icon: 'i-icon-park-outline:inbox',
        permissions: ['stockPool'],
      },
      {
        name: 'Stock Source Files',
        to: 'stock-source-files',
        icon: 'i-icon-park-outline:inbox',
        permissions: ['stockSourceFile'],
      },
      {
        name: 'Stock Source File Downloads',
        to: 'stock-source-file-downloads',
        icon: 'i-icon-park-outline:inbox',
        permissions: ['stockSourceFileDownload'],
      },
      {
        name: 'Product Stocks',
        to: 'product-stocks',
        icon: 'i-icon-park-outline:inbox',
        permissions: ['productStock'],
      },
      {
        name: 'Product Stock Logs',
        to: 'product-stock-logs',
        icon: 'i-icon-park-outline:inbox',
        permissions: ['productStockLog'],
      },
    ],
  },
  {
    name: 'Assistant',
    icon: 'i-mdi:wizard-hat',
    permissions: ['assistant'],
    children: [
      {
        name: 'AI Product Data Generation',
        to: 'assistant-ai-product-data-generation',
        icon: 'i-mdi:wizard-hat',
        permissions: ['assistant'],
        beta: true,
      },
      {
        name: 'AI Product Data Review',
        to: 'assistant-ai-product-data-review',
        icon: 'i-mdi:wizard-hat',
        permissions: ['assistant'],
      },
      {
        name: 'AI Product Data (List)',
        to: 'assistant-ai-product-data',
        icon: 'i-mdi:wizard-hat',
        permissions: ['assistant'],
      },
      {
        name: 'AI Product Data Purge',
        to: 'assistant-ai-product-data-purge',
        icon: 'i-mdi:wizard-hat',
        permissions: ['assistant'],
      },
    ],
  },
  {
    name: 'BI Center',
    to: 'bi-center',
    icon: 'i-icon-park-outline:download',
    permissions: ['bi'],
    beta: true,
    children: [
      {
        name: 'Product Metrics',
        to: 'bi-center-product-metrics',
        icon: 'i-icon-park-outline:weixin-market',
        permissions: ['bi'],
        beta: true,
      },
      {
        name: 'Product POAS',
        to: 'bi-center-product-poas',
        icon: 'i-icon-park-outline:weixin-market',
        permissions: ['bi'],
        beta: true,
      },
      {
        name: 'Margin Checks',
        to: 'bi-center-margin-checks',
        icon: 'i-icon-park-outline:weixin-market',
        permissions: ['bi'],
        beta: true,
      },
      {
        name: 'Performance Stocks',
        to: 'bi-center-performance-stocks',
        icon: 'i-icon-park-outline:weixin-market',
        permissions: ['bi'],
        beta: true,
      },
      {
        name: 'Manufacturer Margins',
        to: 'bi-center-manufacturer-margins',
        icon: 'i-icon-park-outline:weixin-market',
        permissions: ['bi'],
        beta: true,
      },
    ],
  },
  {
    name: 'Channels',
    icon: 'i-icon-park-outline:world',
    permissions: ['channel', 'channelMigration'],
    children: [
      {
        name: 'Channels',
        to: 'channels',
        icon: 'i-icon-park-outline:world',
        permissions: ['channel'],
      },
      {
        name: 'Channel Migration',
        to: 'channels-migration',
        icon: 'i-icon-park-outline:data-switching',
        permissions: ['channelMigration'],
      },
      {
        name: 'Sitemaps',
        to: 'sitemaps',
        icon: 'i-icon-park-outline:map-road-two',
        permissions: ['channelSitemaps'],
      },
    ],
  },
  {
    name: 'Settings',
    icon: 'i-icon-park-outline:tool',
    children: [
      {
        name: 'Purchase webhooks',
        to: 'purchase-webhooks',
        icon: 'i-icon-park-outline:send',
        permissions: ['order'],
      },
      {
        name: 'Links',
        to: 'links',
        icon: 'i-icon-park-outline:link-one',
        permissions: ['link'],
      },
      {
        name: 'Redirects',
        to: 'redirects',
        icon: 'i-icon-park-outline:link-two',
        permissions: ['redirect'],
      },
      {
        name: 'Roles',
        to: 'roles',
        icon: 'i-icon-park-outline:branch-one',
        permissions: ['role'],
      },
      {
        name: 'Mega Menus',
        to: 'menu',
        icon: 'i-icon-park-outline:hamburger-button',
        permissions: ['menu'],
      },
      {
        name: 'Teasers',
        to: 'teasers',
        icon: 'i-icon-park-outline:id-card-h',
        permissions: ['teaser'],
      },
      {
        name: '404 Page Setup',
        to: 'not-found-pages',
        icon: 'i-icon-park-outline:id-card-h',
        permissions: ['teaser'],
      },
      {
        name: 'Tags',
        to: 'tags',
        icon: 'i-icon-park-outline:tag-one',
        permissions: ['tag'],
      },
      {
        name: 'Taxes',
        to: 'taxes',
        icon: 'i-icon-park-outline:expenses',
        permissions: ['tax'],
      },
      {
        name: 'Languages',
        to: 'languages',
        icon: 'i-icon-park-outline:translate',
        permissions: ['language'],
      },
      {
        name: 'Currencies',
        to: 'currencies',
        icon: 'i-icon-park-outline:dollar',
        permissions: ['currency'],
      },
      {
        name: 'Payment Methods',
        to: 'payment-methods',
        icon: 'i-icon-park-outline:wallet',
        permissions: ['paymentMethod'],
      },
      {
        name: 'Delivery Methods',
        to: 'delivery-methods',
        icon: 'i-icon-park-outline:truck',
        permissions: ['deliveryMethod'],
      },
      {
        name: 'Countries',
        to: 'countries',
        icon: 'i-icon-park-outline:flag',
        permissions: ['country'],
      },
      {
        name: 'Manufacturer Deliveries',
        to: 'manufacturer-deliveries',
        icon: 'i-icon-park-outline:transport',
        permissions: ['manufacturerDelivery'],
      },
      {
        name: 'Carriers',
        to: 'carriers',
        icon: 'i-icon-park-outline:set-off',
        permissions: ['carrier'],
      },
      {
        name: 'Partners',
        to: 'partners',
        icon: 'i-icon-park-outline:cooperative-handshake',
        permissions: ['partner'],
      },
      {
        name: 'Campaigns',
        to: 'campaigns',
        icon: 'i-icon-park-outline:calendar-three',
        permissions: ['campaign'],
      },
      {
        name: 'Order Statuses',
        to: 'order-statuses',
        icon: 'i-icon-park-outline:alarm',
        permissions: ['orderStatus'],
      },
      {
        name: 'Templates',
        to: 'templates',
        icon: 'i-icon-park-outline:doc-detail',
        permissions: ['template'],
      },
      {
        name: 'Authors',
        to: 'authors',
        icon: 'i-icon-park-outline:people',
        permissions: ['author'],
      },
      {
        name: 'Users',
        to: 'users',
        icon: 'i-icon-park-outline:weixin-people-nearby',
        permissions: ['users'],
      },
      {
        name: 'HQ stock availability',
        to: 'integrations-hq-stock',
        icon: 'i-icon-park-outline:database-sync',
        permissions: ['channel'],
      },
      {
        name: 'Integrations',
        to: 'integrations',
        icon: 'i-icon-park-outline:blocks-and-arrows',
        permissions: ['integration'],
      },
    ],
  },
  {
    name: 'Translations',
    icon: 'i-icon-park-outline:translate',
    permissions: [
      'translationSettings',
      'translationGlossary',
      'translationEngine',
      'translationInbox',
      'translationBulk',
      'translationReporting',
    ],
    beta: true,
    children: [
      {
        name: 'Dashboard',
        to: 'translations',
        icon: 'i-icon-park-outline:dashboard',
        permissions: ['translationReporting'],
      },
      {
        name: 'Settings',
        to: 'translations-settings',
        icon: 'i-icon-park-outline:tool',
        permissions: ['translationSettings'],
      },
      {
        name: 'Glossary',
        to: 'translations-glossary',
        icon: 'i-icon-park-outline:book-one',
        permissions: ['translationGlossary'],
      },
      {
        name: 'Inbox',
        to: 'translations-inbox',
        icon: 'i-icon-park-outline:inbox',
        permissions: ['translationInbox'],
      },
      {
        name: 'Bulk translate',
        to: 'translations-bulk',
        icon: 'i-icon-park-outline:layers',
        permissions: ['translationBulk'],
      },
      {
        name: 'Jobs',
        to: 'translations-jobs',
        icon: 'i-mdi:wizard-hat',
        permissions: ['translationBulk'],
      },
      {
        name: 'Playground',
        to: 'translations-playground',
        icon: 'i-icon-park-outline:experiment',
        permissions: ['translationEngine'],
        beta: true,
      },
    ],
  },
  {
    name: 'Knowledge Base',
    to: 'knowledge-base',
    icon: 'i-icon-park-outline:book',
    permissions: ['knowledgeBase'],
    beta: true,
  },
  // {
  //   name: 'User Events',
  //   to: 'user-events',
  //   icon: 'i-icon-park-outline:eyes',
  //   permissions: ['user-events'],
  // },
]

const config = useRuntimeConfig()
const store = useStore()
const router = useRouter()

const canSeeTranslationInbox = computed(() =>
  (store.auth?.user?.permissions || []).some(
    (p) =>
      p === 'translationInbox' ||
      p.startsWith('translationInbox:') ||
      p === '**:w' ||
      p === '**',
  ),
)

const translationDashboardQuery = useQuery({
  query: TranslationDashboardDocument,
  variables: {},
  requestPolicy: 'cache-and-network',
  pause: computed(() => !canSeeTranslationInbox.value),
})

const translationInboxBadge = computed(() => {
  const dash = translationDashboardQuery.data.value?.translationDashboard
  if (!dash) return 0
  return (dash.pendingSuggestions || 0) + (dash.staleSuggestions || 0)
})

const isKareBackoffice = computed(
  () =>
    config.public.isKare === true ||
    String(config.public.apiHost ?? '').includes('kare') ||
    String(config.public.gqlHost ?? '').includes('kare'),
)

const navigation = computed(() => {
  let menu = NavigationMenu.map((node) => ({
    ...node,
    ...(node.children && {
      children: node.children.filter((child) =>
        (child.to !== 'purchase-webhooks' || String(config.public.purchaseWebhooksEnabled) === 'true') &&
        (child.to !== 'product-internal-statuses' || !checkPermission('partnerProductEdit', true)),
      ),
    }),
  }))
  if (isKareBackoffice.value) {
    // Kare-only top-level entry (not present in wonder-api).
    menu = [
      ...menu,
      {
        name: 'Speed4Trade',
        to: 'speed4trade',
        icon: 'i-icon-park-outline:cloud-storage',
        permissions: ['speed4tradeImport'],
        beta: true,
      },
    ]
    menu = menu.map((node) => {
      if (node.name === 'OMS') {
        return {
          ...node,
          children: [
            ...(node.children ?? []),
            {
              name: 'SHD Mappings',
              to: 'orders-shd-mappings',
              icon: 'i-icon-park-outline:link-four',
              permissions: ['order'],
              beta: true,
            },
            {
              name: 'Pending Shipments',
              to: 'orders-pending-shipments',
              icon: 'i-icon-park-outline:delivery',
              permissions: ['order'],
              beta: true,
            },
          ],
        }
      }
      if (node.name === 'Channels') {
        return {
          ...node,
          permissions: [...(node.permissions ?? []), 'channelRemoteCategories'],
          children: [
            ...(node.children ?? []),
            {
              name: 'Remote Categories',
              to: 'channels-remote-categories',
              icon: 'i-icon-park-outline:tree-diagram',
              permissions: ['channelRemoteCategories'],
            },
          ],
        }
      }
      return node
    })
  }
  const badge = translationInboxBadge.value
  if (badge > 0) {
    menu = menu.map((node) => {
      if (node.name !== 'Translations' || !node.children) return node
      return {
        ...node,
        badge,
        children: node.children.map((child) =>
          child.to === 'translations-inbox' ? { ...child, badge } : child,
        ),
      }
    })
  }
  return getNavigationItems(menu)(store.auth?.user?.permissions || [])
})

const userNavigation = [
  { name: 'Your Profile', href: '/profile' },
  { name: 'Sign out', href: '/logout' },
]

const sidebarOpen = ref(false)
const fullscreenSidebarOpen = ref(true)
const fullscreenShowSidebarItems = ref(true)

watch(fullscreenSidebarOpen, (value) => {
  if (value === true) {
    setTimeout(() => {
      fullscreenShowSidebarItems.value = true
    }, 100)
  }
  if (value === false) {
    setTimeout(() => {
      fullscreenShowSidebarItems.value = false
    }, 5)
  }
})
provide('fullscreenSidebarOpen', fullscreenSidebarOpen)

const userData = computed(() => {
  const { name, email } = store.auth.user || {}

  return {
    name: name || email,
    letter: name?.[0] || email?.[0],
  }
})

const initLogout = ref(false)
const onLogout = async () => {
  const { isCanceled } = await confirmDialog.reveal({
    title: 'Do you want to logout?',
    description: `We'll miss you!`,
    type: 'info',
  })
  if (!isCanceled) {
    initLogout.value = true
    router.push('/logout')
  }
}

let envName
switch (config.public.env) {
  case 'staging':
    envName = 'staging'
    break
  case 'development':
    envName = 'dev'
    break
  default:
    envName = config.public.envName || 'wonder'
}

const isMac = process.client && navigator.userAgent.includes('Mac')

// Add knowledge base state
const knowledgeBase = useKnowledgeBase()
const isKnowledgeBaseOpen = computed(() => knowledgeBase.isModalOpen.value)
</script>

<template>
  <div
    v-if="!initLogout"
    class="default-layout relative h-screen flex overflow-hidden bg-gray-50"
  >
    <CommandPalette :navigation-items="navigation" />
    <div class="fixed bottom-4 right-4 text-sm text-gray-500 hidden">
      Press
      <kbd class="px-2 py-1 bg-gray-100 rounded">{{
        isMac ? '⌘' : 'Ctrl'
      }}</kbd>
      + <kbd class="px-2 py-1 bg-gray-100 rounded">K</kbd> to search
    </div>
    <TransitionRoot :show="sidebarOpen" as="template">
      <Dialog
        as="div"
        class="fixed inset-0 flex z-40 xl:hidden"
        @close="sidebarOpen = false"
      >
        <TransitionChild
          as="template"
          enter="transition-opacity ease-linear duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-gray-600 bg-opacity-75" />
        </TransitionChild>
        <TransitionChild
          as="template"
          enter="transition ease-in-out duration-300 transform"
          enter-from="-translate-x-full"
          enter-to="translate-x-0"
          leave="transition ease-in-out duration-300 transform"
          leave-from="translate-x-0"
          leave-to="-translate-x-full"
        >
          <div
            class="relative flex-1 flex flex-col max-w-xs w-full pt-5 pb-4 bg-secondary-900"
          >
            <TransitionChild
              as="template"
              enter="ease-in-out duration-300"
              enter-from="opacity-0"
              enter-to="opacity-100"
              leave="ease-in-out duration-300"
              leave-from="opacity-100"
              leave-to="opacity-0"
            >
              <div class="absolute top-0 right-0 -mr-12 pt-2">
                <button
                  class="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                  type="button"
                  @click="sidebarOpen = false"
                >
                  <span class="sr-only">Close sidebar</span>
                  <div
                    aria-hidden="true"
                    class="i-heroicons-solid-x h-6 w-6 text-white"
                  />
                </button>
              </div>
            </TransitionChild>
            <div class="flex-shrink-0 flex items-center px-6 space-x-1.5">
              <span class="text-2xl font-bold text-white">wonder</span>
            </div>
            <div class="mt-5 flex-1 h-0 overflow-y-auto">
              <Transition>
                <SidebarNav
                  v-show="fullscreenShowSidebarItems"
                  :is-collapsed="!fullscreenSidebarOpen"
                  :items="navigation"
                  @item-clicked="sidebarOpen = false"
                />
              </Transition>
            </div>
          </div>
        </TransitionChild>
        <div aria-hidden="true" class="flex-shrink-0 w-14">
          <!-- Dummy element to force sidebar to shrink to fit close icon -->
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- Static sidebar for desktop -->
    <div class="hidden xl:flex xl:flex-shrink-0 bg-secondary-900">
      <div
        :class="{
          'w-[300px]': fullscreenSidebarOpen,
          'w-16': !fullscreenSidebarOpen,
        }"
        class="flex flex-col transition-all duration-100"
      >
        <!-- Sidebar component, swap this element with another sidebar if you like -->
        <div class="flex-1 flex flex-col min-h-0">
          <div
            :class="{
              'p-6 w-full text-center': fullscreenSidebarOpen,
              'px-4 py-6': !fullscreenSidebarOpen,
            }"
            class="flex items-center flex-shrink-0 bg-secondary-900 space-x-1.5 relative"
          >
            <Transition>
              <span
                v-show="fullscreenShowSidebarItems"
                class="text-2xl font-bold text-white select-none cursor-default whitespace-nowrap"
                >\o/ {{ envName }}</span
              >
            </Transition>

            <i
              v-show="fullscreenSidebarOpen"
              class="i-icon-park-outline:double-left !bg-white text-white text-2xl font-bold cursor-pointer h-8 w-8 right-4 top-6 absolute"
              @click="fullscreenSidebarOpen = false"
            />
            <i
              v-show="!fullscreenSidebarOpen"
              class="i-icon-park-outline:double-right !bg-white text-white text-2xl font-bold cursor-pointer h-8 w-8 right-4 top-6 absolute"
              @click="fullscreenSidebarOpen = true"
            />
          </div>
          <div
            class="flex-1 flex flex-col pb-4 overflow-y-auto scrollbar:!w-3 scrollbar:!h-1.5 scrollbar-track:!bg-secondary-800 scrollbar-thumb:!rounded-full scrollbar-thumb:!bg-secondary-700"
          >
            <Transition>
              <SidebarNav
                :is-collapsed="!fullscreenSidebarOpen"
                :items="navigation"
              />
            </Transition>
          </div>
          <div
            v-show="fullscreenShowSidebarItems"
            class="bg-secondary-900 px-6 py-4 border-t border-gray-800"
          >
            <div
              class="max-w-xs flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-primary-500"
            >
              <span class="sr-only">Open user menu</span>
              <div
                class="flex justify-center items-center h-8 w-8 rounded-full text-white font-bold bg-primary-600 uppercase"
              >
                {{ userData.letter }}
              </div>
              <div class="ml-3 text-left">
                <p
                  class="text-sm font-medium text-gray-200 group-hover:text-gray-900"
                >
                  {{ userData.name }}
                </p>
                <NuxtLink
                  :to="'/profile'"
                  class="text-xs font-medium text-gray-400 group-hover:text-gray-700"
                >
                  View profile
                </NuxtLink>
              </div>
              <div class="ml-auto">
                <Button
                  class="flex justify-between space-x-2 group bg-secondary-800 w-full"
                  icon="i-heroicons-outline-logout text-lg"
                  variant="simple"
                  @click="onLogout"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-col w-0 flex-1 overflow-hidden">
      <div
        class="xl:hidden relative z-30 flex-shrink-0 flex h-16 bg-white shadow lg:px-2"
      >
        <button
          class="px-4 md:pl-6 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
          type="button"
          @click="sidebarOpen = true"
        >
          <span class="sr-only">Open sidebar</span>
          <div
            aria-hidden="true"
            class="i-heroicons-outline-menu-alt-2 text-xl"
          />
        </button>
        <div
          class="flex-1 pr-4 md:pr-6 flex justify-between items-center space-x-2"
        >
          <div class="ml-4 flex justify-end items-center lg:ml-6 flex-shrink-0">
            <!-- Profile dropdown -->
            <Menu as="div" class="ml-3 relative">
              <div>
                <MenuButton
                  class="max-w-xs flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-primary-500"
                >
                  <span class="sr-only">Open user menu</span>
                  <span class="hidden md:inline px-2 font-medium text-gray-600">
                    {{ userData.name }}
                  </span>
                  <div
                    class="flex justify-center items-center h-8 w-8 rounded-full text-white font-bold bg-primary-600 uppercase"
                  >
                    {{ userData.letter }}
                  </div>
                </MenuButton>
              </div>
              <transition
                enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95"
              >
                <MenuItems
                  class="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
                >
                  <MenuItem
                    v-for="item in userNavigation"
                    :key="item.name"
                    v-slot="{ active }"
                  >
                    <NuxtLink
                      :class="[
                        active ? 'bg-gray-100' : '',
                        'block px-4 py-2 text-sm text-gray-700',
                      ]"
                      :to="item.href"
                    >
                      {{ item.name }}
                    </NuxtLink>
                  </MenuItem>
                </MenuItems>
              </transition>
            </Menu>
          </div>
        </div>
      </div>

      <main
        class="flex-1 relative overflow-y-auto focus:outline-none scrollbar:!w-3 scrollbar:!h-1.5 scrollbar-track:!bg-gray-200 scrollbar-thumb:!rounded-full scrollbar-thumb:!bg-gray-300 scrollbar-thumb-hover:!bg-gray-400 pb-16"
        :class="{ 'knowledge-base-open': isKnowledgeBaseOpen }"
      >
        <div>
          <slot />
        </div>
      </main>
    </div>

    <KnowledgeBaseButton />
    <KnowledgeBaseSidebar />
  </div>
</template>
<style lang="postcss">
.default-layout {
  .formkit-inner {
    input[type='checkbox']:checked:hover,
    input[type='checkbox']:checked:focus,
    input[type='radio']:checked:hover,
    input[type='radio']:checked:focus {
      @apply bg-primary-500;
    }
  }
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.15s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

/* Knowledge base transitions */
main {
  transition: padding-right 0.3s ease-in-out;
}

main.knowledge-base-open {
  padding-right: 24rem; /* 384px in rem units (96 * 4 = 384) */
}

/* For smaller screens, don't adjust the padding */
@media (max-width: 1023px) {
  main.knowledge-base-open {
    padding-right: 0;
  }
}

/* Hide scrollbar in collapsed state */
.w-16 [class*='scrollbar'] {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.w-16 [class*='scrollbar']::-webkit-scrollbar {
  display: none;
}
</style>
